import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { customInstance } from '@/api/http';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';
import { useGetDashboardOverview, useGetDashboardTimeseries } from '@/api/generated/dashboard/dashboard';
import type { GetDashboardOverviewParams, GetDashboardTimeseriesParams } from '@/api/generated/model';

export type {
  DashboardOverviewResponseDTO,
  DashboardTimeseriesResponseDTO,
} from '@/api/generated/model';

/** Extended until orval regen picks up Phase 4 overview fields. */
export type DashboardOverviewWithPhase4 = import('@/api/generated/model').DashboardOverviewResponseDTO & {
  visitorsDeltaPercent?: number | null;
  visitorsDataSource?: 'CACHE' | 'LIVE' | string | null;
  activeUsersDataSource?: 'CACHE' | 'LIVE' | string | null;
  postHogSyncedAt?: string | null;
};

export type DashboardBreakdownItem = {
  key?: string;
  label?: string;
  count?: number;
  percent?: number | null;
  deltaPercent?: number | null;
};

export type DashboardSignupsByLocationResponse = {
  days?: number | null;
  startDate?: string;
  endDate?: string;
  countryCode?: string | null;
  total?: number;
  dataSourceNote?: string | null;
  items?: DashboardBreakdownItem[];
};

export type DashboardSignupsByRegionResponse = {
  days?: number | null;
  startDate?: string;
  endDate?: string;
  countryCode?: string | null;
  total?: number;
  dataSourceNote?: string | null;
  items?: DashboardBreakdownItem[];
};

export type DashboardSignupMethodsResponse = {
  days?: number | null;
  startDate?: string;
  endDate?: string;
  countryCode?: string | null;
  total?: number;
  dataSourceNote?: string | null;
  items?: DashboardBreakdownItem[];
};

export type DashboardTopCreator = {
  userId?: number;
  displayName?: string;
  username?: string;
  avatarUrl?: string | null;
  followerCount?: number;
  rank?: number;
};

export type DashboardTopCreatorsResponse = {
  countryCode?: string | null;
  limit?: number;
  items?: DashboardTopCreator[];
};

export type DashboardHeatmapCell = {
  dayOfWeek?: number;
  hour?: number;
  count?: number;
};

export type DashboardActivityHeatmapResponse = {
  days?: number | null;
  startDate?: string;
  endDate?: string;
  countryCode?: string | null;
  timezone?: string;
  status?: 'AVAILABLE' | 'COMING_SOON' | 'UNAVAILABLE' | string;
  dataSource?: 'CACHE' | 'LIVE' | string | null;
  qualifyingEvents?: string[];
  cells?: DashboardHeatmapCell[];
  totalEvents?: number;
};

/** GET /admin/api/dashboard/activity-heatmap — Phase 4. */
export function useDashboardActivityHeatmap(
  days?: number | null,
  countryCode?: string,
  timezone?: string,
) {
  const params: Record<string, string | number> = {};
  if (days != null) params.days = days;
  if (countryCode) params.countryCode = countryCode;
  if (timezone) params.timezone = timezone;
  const queryParams = Object.keys(params).length > 0 ? params : undefined;
  return useQuery({
    queryKey: ['/admin/api/dashboard/activity-heatmap', queryParams ?? {}],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<DashboardActivityHeatmapResponse>>({
        url: '/admin/api/dashboard/activity-heatmap',
        method: 'GET',
        params: queryParams,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
  });
}

function overviewParams(
  range?: { days?: number | null; startDate?: string; endDate?: string } | number | null,
  countryCode?: string,
): GetDashboardOverviewParams | undefined {
  const params: GetDashboardOverviewParams = {};
  if (range != null && typeof range === 'object') {
    if (range.days != null) params.days = range.days;
    if (range.startDate) params.startDate = range.startDate;
    if (range.endDate) params.endDate = range.endDate;
  } else if (typeof range === 'number') {
    params.days = range;
  }
  if (countryCode) params.countryCode = countryCode;
  return Object.keys(params).length > 0 ? params : undefined;
}

/**
 * Platform-wide KPI totals — GET /admin/api/dashboard/overview.
 */
export function useDashboardOverview(
  range?: { days?: number | null; startDate?: string; endDate?: string } | number | null,
  countryCode?: string,
) {
  return useGetDashboardOverview(overviewParams(range, countryCode), {
    query: { select: selectData, placeholderData: keepPreviousData },
  });
}

/**
 * Bucketed activity for sparklines / hero chart — GET /admin/api/dashboard/timeseries.
 */
export function useDashboardTimeseries(
  days: number,
  countryCode?: string,
) {
  const params: GetDashboardTimeseriesParams = { days };
  if (countryCode) params.countryCode = countryCode;
  return useGetDashboardTimeseries(params, {
    query: { select: selectData, placeholderData: keepPreviousData },
  });
}

function phase3Params(days?: number | null, countryCode?: string) {
  const params: Record<string, string | number> = {};
  if (days != null) params.days = days;
  if (countryCode) params.countryCode = countryCode;
  return Object.keys(params).length > 0 ? params : undefined;
}

/** GET /admin/api/dashboard/signups-by-location — signup IP country (D9). */
export function useDashboardSignupsByLocation(days?: number | null, countryCode?: string) {
  const params = phase3Params(days, countryCode);
  return useQuery({
    queryKey: ['/admin/api/dashboard/signups-by-location', params ?? {}],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<DashboardSignupsByLocationResponse>>({
        url: '/admin/api/dashboard/signups-by-location',
        method: 'GET',
        params,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
  });
}

/** GET /admin/api/dashboard/signups-by-region — signup IP region/state (D9). */
export function useDashboardSignupsByRegion(days?: number | null, countryCode?: string) {
  const params = phase3Params(days, countryCode);
  return useQuery({
    queryKey: ['/admin/api/dashboard/signups-by-region', params ?? {}],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<DashboardSignupsByRegionResponse>>({
        url: '/admin/api/dashboard/signups-by-region',
        method: 'GET',
        params,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
    enabled: Boolean(countryCode),
  });
}

/** GET /admin/api/dashboard/signup-methods — Phase 3. */
export function useDashboardSignupMethods(days?: number | null, countryCode?: string) {
  const params = phase3Params(days, countryCode);
  return useQuery({
    queryKey: ['/admin/api/dashboard/signup-methods', params ?? {}],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<DashboardSignupMethodsResponse>>({
        url: '/admin/api/dashboard/signup-methods',
        method: 'GET',
        params,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
  });
}

/** GET /admin/api/dashboard/top-creators — Phase 3. */
export function useDashboardTopCreators(limit = 5, countryCode?: string) {
  const params: Record<string, string | number> = { limit };
  if (countryCode) params.countryCode = countryCode;
  return useQuery({
    queryKey: ['/admin/api/dashboard/top-creators', params],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<DashboardTopCreatorsResponse>>({
        url: '/admin/api/dashboard/top-creators',
        method: 'GET',
        params,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
  });
}

export type DashboardPostHogResyncResponse = {
  status?: 'OK' | 'COMING_SOON' | 'RATE_LIMITED' | 'FAILED' | string;
  message?: string;
  fromDay?: string;
  toDayExclusive?: string;
  countryCode?: string | null;
  timezone?: string;
  metricVersion?: number;
  dailyRowsUpserted?: number;
  heatmapRowsUpserted?: number;
  windowRowsUpserted?: number;
  syncedAt?: string;
};

/** Matches tipmi-admin-api PostHogDashboardSyncService.RESYNC_COOLDOWN. */
export const POSTHOG_RESYNC_COOLDOWN_MS = 2 * 60 * 1000;

/** POST /admin/api/dashboard/posthog/resync — force MySQL aggregate refresh from PostHog. */
export async function resyncDashboardPostHog(body?: {
  days?: number;
  countryCode?: string;
  timezone?: string;
  includeToday?: boolean;
}): Promise<DashboardPostHogResyncResponse> {
  try {
    const res = await customInstance<SuccessResponseDTO<DashboardPostHogResyncResponse>>({
      url: '/admin/api/dashboard/posthog/resync',
      method: 'POST',
      data: body ?? { days: 30, includeToday: true },
      // Avoid axios treating 429 as an unhandled throw when the envelope still carries status.
      validateStatus: (status) => (status >= 200 && status < 300) || status === 429,
    });
    // 429 responses still return the SuccessResponseDTO body from tipmi-admin-api.
    if (
      res &&
      typeof res === 'object' &&
      'httpStatusCode' in res &&
      (res as SuccessResponseDTO<DashboardPostHogResyncResponse>).httpStatusCode === 429
    ) {
      const data = selectData(res as SuccessResponseDTO<DashboardPostHogResyncResponse>);
      return data?.status
        ? data
        : { status: 'RATE_LIMITED', message: 'Resync cooldown active — wait a couple of minutes' };
    }
    return selectData(res as SuccessResponseDTO<DashboardPostHogResyncResponse>);
  } catch (err) {
    // Fallback if interceptor/axios still throws on 429
    const ax = err as { response?: { status?: number; data?: SuccessResponseDTO<DashboardPostHogResyncResponse> } };
    if (ax.response?.status === 429) {
      const data = ax.response.data?.data;
      return (
        data ?? {
          status: 'RATE_LIMITED',
          message: 'Resync cooldown active — wait a couple of minutes',
        }
      );
    }
    throw err;
  }
}
